import {
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter, Button, FormLabel, FormControl, Select, useToast, Input, Box, HStack, Badge,
} from "@chakra-ui/react";
import React, {useMemo, useState} from "react";
import {
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from "react-table";
import Moment from 'moment';

// Custom components
import Card from "components/card/Card";

// Assets
import {rentalTypes} from "../../variables/rentalTypes";
import RentalStatus from "../rental/RentalStatus";
import {deleteData, putData} from "../../services/api";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {NavLink} from "react-router-dom";

import {
    FaFileCsv,
    FaTrash,
} from "react-icons/fa";
import {RENTAL_STATUS} from "../../variables/rentalStatus";
import { saveAs } from 'file-saver';

export default function ColumnsTable(props) {
    const {columnsData, tableData, onDelete} = props;
    const {isOpen, onOpen, onClose} = useDisclosure()

    const toast = useToast()
    const MySwal = withReactContent(Swal)

    const [selectedRental, setSelectedRental] = useState(null);
    const [statusSelected, setStatusSelected] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const columns = useMemo(() => columnsData, [columnsData]);
    const data = useMemo(() => tableData, [tableData]);
    const filteredData = useMemo(() => {
        return selectedStatuses.length > 0
            ? tableData.filter(row => selectedStatuses.includes(row.status))
            : tableData;
    }, [tableData, selectedStatuses]);

    const initialState = {
        pageSize: 100,
        pageIndex: 0
    };

    const tableInstance = useTable(
        {
            columns,
            data: filteredData,
            initialState
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
        setGlobalFilter
    } = tableInstance;

    const handleStatusClick = (rental) => {
        setSelectedRental(rental)
        onOpen()
    };

    const handleDeleteClick = (rental) => {
        MySwal.fire({
            title: <p>Löschung bestätigen</p>,
            html: <p>Inserat {rental.id} wirklich löschen?</p>,
            confirmButtonText: 'Inserat jetzt löschen',
            confirmButtonColor: '#d33',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Abbrechen',
            preConfirm: async () => {
                Swal.showLoading();
                return handleConfirmDeleteClick(rental.id);
            }
        })
    };

    const handleConfirmDeleteClick = async (rentalId) => {
        try {
            await deleteData('/api/rentals/' + rentalId);
            setLoading(null)
            onDelete(rentalId);

            toast({
                title: "Inserat wurde gelöscht.",
                description: `Das Inserat wurde erfolgreich gelöscht.`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            // setError(error.message);
        }
    };

    const handleChange = (e) => {
        setStatusSelected(e.target.value);
    };

    const handleSetSearchTerm = (searchTerm) => {
        setGlobalFilter(searchTerm)
    };


    function onSubmit() {
        return new Promise(async (resolve) => {
            try {
                setLoading(true)
                await putData(`/api/rentals/${selectedRental.id}/status`, {status: statusSelected});
                setLoading(false)
                resolve();

                onClose()

                MySwal.fire({
                    title: <p>Status wurde angepasst</p>,
                    html: <p>Der Status wurde erfolgreich angepasst.</p>,
                    confirmButtonText: 'Daten aktualisieren',
                    icon: 'success',
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.reload();
                    }
                })

            } catch (error) {
                toast({
                    title: 'Status konnte nicht angepasst werden: ' + error.message,
                    status: 'error',
                    isClosable: true,
                })
                setLoading(false)
                resolve();
            }
        })
    }

    const getMatchingRentalNames = (cellValue) => {
        return rentalTypes
            .filter(type => cellValue.includes(type.id))
            .map(type => type.name)
            .join(', ');
    };

    const toggleFilter = (status) => {
        setSelectedStatuses((prev) => {
            return prev.includes(status)
                ? prev.filter((s) => s !== status)
                : [status];
        });
    };

    const downloadCSV = () => {
        if (!page.length) {
            alert("Keine Daten zum Exportieren!");
            return;
        }

        const selectedHeaders = ['id', 'user', 'email'];

        const headers = selectedHeaders.join(";");

        const rows = page.map(row =>
            selectedHeaders.map(header => row.original[header] || '').join(";")
        );

        const csvContent = [headers, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "export.csv");
    };

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            overflowX={{sm: "scroll", lg: "hidden"}}>

            <Flex>
                <Flex gap={2} mb={5} alignItems='center' ml={5}>
                    <Badge
                        onClick={() => toggleFilter(RENTAL_STATUS.ONLINE)}
                        colorScheme='green'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(RENTAL_STATUS.ONLINE) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === RENTAL_STATUS.ONLINE).length} Inseriert
                    </Badge>
                    <Badge
                        onClick={() => toggleFilter(RENTAL_STATUS.OFFLINE)}
                        colorScheme='red'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(RENTAL_STATUS.OFFLINE) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === RENTAL_STATUS.OFFLINE).length} Nicht Inseriert
                    </Badge>
                    <Badge
                        onClick={() => toggleFilter(RENTAL_STATUS.UNFINISHED)}
                        colorScheme='purple'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(RENTAL_STATUS.UNFINISHED) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === RENTAL_STATUS.UNFINISHED).length} In Bearbeitung
                    </Badge>
                    <Button onClick={downloadCSV}>
                        <FaFileCsv/>
                    </Button>
                </Flex>
                <FormControl width={200} mb='24px' ml={'auto'} mr={5} display='flex' alignItems={'center'}>
                    <FormLabel
                        ms='4px'
                        fontSize='sm'
                        fontWeight='500'
                        color={textColor}
                        htmlFor='search'
                    >
                        Suche
                    </FormLabel>
                    <Input
                        id='search'
                        fontSize='sm'
                        type='text'
                        fontWeight='500'
                        size='lg'
                        onChange={(e) => handleSetSearchTerm(e.target.value)}
                    />
                </FormControl>
            </Flex>

            <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px'>
                <Thead>
                    {headerGroups.map((headerGroup, index) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    pe='10px'
                                    key={index}
                                    borderColor={borderColor}>
                                    <Flex
                                        align='center'
                                        fontSize={{sm: "10px", lg: "12px"}}
                                        color='gray.400'>
                                        {column.render("Header")}
                                    </Flex>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()} key={index}>
                                {row.cells.map((cell, index) => {
                                    let data;
                                    if (cell.column.Header === "ID") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                <NavLink to={"/user/rental/" + row.original.id}>{cell.value}</NavLink>
                                            </Text>
                                        );
                                    } else if (cell.column.Header === "TITEL") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>{cell.value}</Text>
                                        );
                                    } else if (cell.column.Header === "TYP") {
                                        data = (
                                            <Text color={textColor} fontSize='sm'
                                                  fontWeight='700'>{getMatchingRentalNames(cell.value)}</Text>
                                        );
                                    } else if (cell.column.Header === "STATUS") {
                                        data = (
                                            <Flex align='center'>
                                                <Button onClick={() => handleStatusClick(row.original)}>
                                                    <RentalStatus status={cell.value}/>
                                                </Button>
                                            </Flex>
                                        );
                                    } else if (cell.column.Header === "ANGELEGT AM") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {Moment(cell.value).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        );
                                    } else {
                                        data = (
                                            <Text>
                                                {cell.value}
                                            </Text>
                                        );
                                    }
                                    return (
                                        <Td
                                            {...cell.getCellProps()}
                                            key={index}
                                            fontSize={{sm: "14px"}}
                                            minW={{sm: "150px", md: "200px", lg: "auto"}}
                                            borderColor='transparent'>
                                            {data}
                                        </Td>
                                    );
                                })}
                                <Td><Button onClick={() => handleDeleteClick(row.original)}><FaTrash /></Button></Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            <Box className="pagination" mt={4}>
                <Flex alignItems={'center'} justifyContent="space-between" gap={{base:5, lg:20}} direction={{base: "column", lg: 'row'}} px={5}>
                    <HStack spacing={4}>
                        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} colorScheme="brand" size="sm">
                            {"<<"}
                        </Button>
                        <Button onClick={() => previousPage()} disabled={!canPreviousPage} colorScheme="brand"
                                size="sm">
                            {"<"}
                        </Button>
                        <Button onClick={() => nextPage()} disabled={!canNextPage} colorScheme="brand" size="sm">
                            {">"}
                        </Button>
                        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} colorScheme="brand"
                                size="sm">
                            {">>"}
                        </Button>
                    </HStack>

                    <Text>
                        <strong>
                            {pageIndex + 1} von {pageOptions.length}
                        </strong>
                    </Text>

                    <HStack spacing={4}>

                        <Text>Seite: </Text>
                        <Input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(page);
                            }}
                            width="100px"
                            size="sm"
                        />
                        <Select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            width="fit-content"
                            size="sm"
                        >
                            {[10, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Zeige {pageSize}
                                </option>
                            ))}
                        </Select>
                    </HStack>


                </Flex>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay/>
                <ModalContent bgColor="white">
                    <ModalHeader>{selectedRental?.title}</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <Flex mb={5} gap={10}>
                            <Text
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}>Gültig bis:</Text>
                            <Text>{Moment(selectedRental?.date_valid).format('DD.MM.YYYY')}</Text>
                        </Flex>
                        <Flex mb={5} gap={10}>
                            <Text
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}>Aktueller Status:</Text>
                            <RentalStatus status={selectedRental?.status}/>
                        </Flex>
                        <FormControl mb='24px'>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='title'
                            >
                                Neuer Status
                            </FormLabel>
                            <Select
                                id='type'
                                fontSize='sm'
                                type='text'
                                fontWeight='500'
                                size='lg'
                                value={statusSelected}  // bind the selected value to the state
                                onChange={handleChange} // handle the change event
                            >
                                <option value="0">nicht inseriert</option>
                                <option value="1">inseriert</option>
                            </Select>
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant='ghost' mr={3} onClick={onClose}>
                            Schließen
                        </Button>
                        <Button isLoading={loading} variant='brand' onClick={onSubmit}>Absenden</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Card>
    );
}
